// ---------------------------------------------------------------------------
// Campo de formulário controlado, com erro INLINE.
//
// Existe para que a política do FC4 §B.3 (422 com `field` → inline, com
// `aria-invalid`) não precise ser reescrita em cada tela. Consome direto o
// `fieldErrors` do `ApiError`:
//
//    <FormField ... error={fieldErrors.current_password} />
//
// Só o essencial — se uma tela precisar de layout mais rico (grid de 12
// colunas, seções numeradas), o caminho é o composer do FC7 cap.2, não inchar
// este componente.
// ---------------------------------------------------------------------------

import React, { useId } from 'react';
import PropTypes from 'prop-types';

import { Field, Label, Input, HelperText, FieldError } from '../../../styles/ui/Form';

export default function FormField({
   label,
   value,
   onChange,
   error,
   hint,
   type,
   name,
   disabled,
   autoComplete,
   autoFocus,
   placeholder,
   inputMode,
   maxLength,
}) {
   const reactId = useId();
   const inputId = name ? `field-${name}` : reactId;
   const errorId = `${inputId}-error`;
   const hintId = `${inputId}-hint`;

   const describedBy = [error ? errorId : null, hint ? hintId : null]
      .filter(Boolean)
      .join(' ');

   return (
      <Field>
         <Label as="label" htmlFor={inputId}>
            {label}
         </Label>

         <Input
            id={inputId}
            name={name}
            type={type}
            value={value}
            onChange={onChange}
            disabled={disabled}
            autoComplete={autoComplete}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus={autoFocus}
            placeholder={placeholder}
            inputMode={inputMode}
            maxLength={maxLength}
            $invalid={Boolean(error)}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy || undefined}
         />

         {error ? <FieldError id={errorId}>{error}</FieldError> : null}
         {hint ? <HelperText id={hintId}>{hint}</HelperText> : null}
      </Field>
   );
}

FormField.propTypes = {
   label: PropTypes.string.isRequired,
   value: PropTypes.string.isRequired,
   onChange: PropTypes.func.isRequired,
   error: PropTypes.string,
   hint: PropTypes.node,
   type: PropTypes.string,
   name: PropTypes.string,
   disabled: PropTypes.bool,
   autoComplete: PropTypes.string,
   autoFocus: PropTypes.bool,
   placeholder: PropTypes.string,
   inputMode: PropTypes.string,
   maxLength: PropTypes.number,
};

FormField.defaultProps = {
   error: null,
   hint: null,
   type: 'text',
   name: undefined,
   disabled: false,
   autoComplete: undefined,
   autoFocus: false,
   placeholder: undefined,
   inputMode: undefined,
   maxLength: undefined,
};
